import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import css from './WaitList.css';
import StyledButton from 'common/StyledButton';
import { urlParams } from 'utils/request';
import useStarfishAPI from '../hooks/useStarfishAPI';
import useError from '../hooks/useError';
import ConfirmDialog from '../common/ConfirmDialog';
import WaitTable from './WaitTable';

function WaitList(props) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Declarative callbacks for the starfish API
  const [{error: dequeueError, success: dequeueSuccess}, callDequeueAPI] = useStarfishAPI();
  const [{error: cancelError, success: cancelSuccess}, callCancelAppointmentAPI] = useStarfishAPI();

  // Listen to error and call props.setError whenever there is a change
  useError(dequeueError, props.setError);

  // This API will determine if the group session associated with this check-in needs to be cancelled, and cancel it
  const cancelGroupSession = () => {
    const cancelURL = '../kiosk/removeGroupSessionParticipant.html';
    callCancelAppointmentAPI(cancelURL, {
      method: 'POST',
      body: urlParams({
        studentId: props.studentId,
        appointmentId: props.appointmentId
      })
    });
  };

  useEffect(() => {
    const hasGroupSession = props.appointmentId > 0 && props.scheduleBlockId > 0;
    if ( dequeueSuccess ) {
      // If the student had a group session and checked out successfully, try to cancel the appointment
      // If there was no appointment, skip cancelling and go to confirmation page
      hasGroupSession ? cancelGroupSession() : props.goToCheckoutConfirmation();
    }
  }, [dequeueSuccess, props.appointmentId, props.scheduleBlockId]);

  // If the appointment was cancelled go to confirmation page, pass error if error occurred
  useEffect(() => {
    if ( cancelSuccess ) props.goToCheckoutConfirmation();
    else if ( cancelError ) props.goToCheckoutConfirmation(cancelError);
  }, [cancelSuccess, cancelError]);

  const onCheckoutClick = () => {
    setIsDialogOpen(false);
    const dequeueURL = '../kiosk/removeFromQueue.html';
    callDequeueAPI(dequeueURL, {
      method: 'POST',
      body: urlParams({
        kioskId: props.kioskId,
        serviceId: props.serviceId,
        studentId: props.studentId,
        id: props.waitingRoomQueueId,
        scheduleBlockId: props.scheduleBlockId,
        appointmentId: props.appointmentId
      })
    });
  };

  return (
    <div className={css['container']}>
      <WaitTable
        kioskId={props.kioskId}
        setError={props.setError}
        serviceId={props.serviceId}
        studentId={props.studentId}
      />
      <div className={css['buttons-container']}>
        <StyledButton inverted onClick={() => setIsDialogOpen(true)}>
          Check Out
        </StyledButton>
        <StyledButton onClick={props.goToWaitListConfirmation}>
          Keep Waiting
        </StyledButton>
      </div>
      <ConfirmDialog
        appElementId="react-kiosk-container"
        isOpen={isDialogOpen}
        onConfirm={onCheckoutClick}
        onClose={() => setIsDialogOpen(false)}
        confirmText="Yes, check out"
        headerText="Are you sure you want to check out?"
        descriptionText="You will lose your spot in the wait list."
      />
    </div>
  );
}

WaitList.propTypes = {
  studentId: PropTypes.number.isRequired,
  kioskId: PropTypes.number.isRequired,
  setError: PropTypes.func.isRequired,
  serviceId: PropTypes.number.isRequired,
  goToCheckoutConfirmation: PropTypes.func.isRequired,
  goToWaitListConfirmation: PropTypes.func.isRequired,
  appointmentId: PropTypes.number.isRequired,
  waitingRoomQueueId: PropTypes.number.isRequired,
  setWaitingRoomQueueId: PropTypes.func.isRequired,
  scheduleBlockId: PropTypes.number.isRequired,
};

export default WaitList;
